import {
    CONSENT_CHANGE_EVENT,
    CONSENT_OPEN_EVENT,
    CONSENT_STORAGE_KEY,
    CONSENT_VERSION,
    GOOGLE_COOKIE_PREFIXES
} from '@/constants/analytics'

export type ConsentChoice = 'granted' | 'denied'

export interface ConsentRecord {
    choice: ConsentChoice
    version: number
    decidedAt: string
}

const isBrowser = (): boolean => typeof window !== 'undefined'

const noop = (): void => {}

/**
 * Reads the stored decision. Returns `null` when the visitor has not chosen yet,
 * when storage is unavailable, or when the stored consent version is stale.
 */
export const readConsent = (): ConsentRecord | null => {
    if (!isBrowser()) return null

    try {
        const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY)
        if (!raw) return null

        const parsed: unknown = JSON.parse(raw)
        if (typeof parsed !== 'object' || parsed === null) return null

        const record = parsed as Partial<ConsentRecord>
        if (record.choice !== 'granted' && record.choice !== 'denied') return null
        if (record.version !== CONSENT_VERSION) return null

        return {
            choice: record.choice,
            version: record.version,
            decidedAt: typeof record.decidedAt === 'string' ? record.decidedAt : ''
        }
    } catch {
        return null
    }
}

/** Persists the decision and notifies every listener in the page. */
export const writeConsent = (choice: ConsentChoice): void => {
    if (!isBrowser()) return

    const record: ConsentRecord = {
        choice,
        version: CONSENT_VERSION,
        decidedAt: new Date().toISOString()
    }

    try {
        window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
    } catch {
        // Private mode or storage blocked. Still notify listeners so the choice
        // applies for this page view, it simply will not be remembered.
    }

    window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_CHANGE_EVENT, { detail: choice }))
}

export const subscribeToConsent = (listener: (choice: ConsentChoice) => void): (() => void) => {
    if (!isBrowser()) return noop

    const handler = (event: Event): void => {
        const { detail } = event as CustomEvent<ConsentChoice>
        if (detail === 'granted' || detail === 'denied') listener(detail)
    }

    window.addEventListener(CONSENT_CHANGE_EVENT, handler)

    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler)
}

/** Re-opens the cookie banner, used by the "Cookie settings" links. */
export const openCookieSettings = (): void => {
    if (!isBrowser()) return

    window.dispatchEvent(new Event(CONSENT_OPEN_EVENT))
}

export const subscribeToSettingsOpen = (listener: () => void): (() => void) => {
    if (!isBrowser()) return noop

    window.addEventListener(CONSENT_OPEN_EVENT, listener)

    return () => window.removeEventListener(CONSENT_OPEN_EVENT, listener)
}

/**
 * Tells Google Consent Mode about the decision. Safe to call before GTM loads:
 * `gtag` is defined by the bootstrap script in `_document`, so the update is
 * queued on the data layer and applied the moment the container initialises.
 */
export const applyConsentToGoogle = (choice: ConsentChoice): void => {
    if (!isBrowser() || typeof window.gtag !== 'function') return

    window.gtag('consent', 'update', {
        ad_storage: choice,
        ad_user_data: choice,
        ad_personalization: choice,
        analytics_storage: choice,
        functionality_storage: choice,
        personalization_storage: choice
    })
}

/** Expires any Google analytics/ads cookies already on the device. */
export const clearGoogleCookies = (): void => {
    if (!isBrowser()) return

    const { hostname } = window.location
    const segments = hostname.split('.')

    const domains = new Set<string>([hostname])
    if (segments.length > 1) domains.add(`.${segments.slice(-2).join('.')}`)

    document.cookie.split(';').forEach(entry => {
        const name = entry.split('=')[0]?.trim()
        if (!name) return
        if (!GOOGLE_COOKIE_PREFIXES.some(prefix => name.startsWith(prefix))) return

        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`

        domains.forEach(domain => {
            document.cookie = `${name}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`
        })
    })
}
